import { api } from '../api';
import { getCurrentRole, getCurrentUser, getDisplayName, setAuthHint } from '../auth';
import { renderNav } from '../nav';
import { canAccess, isSubsRole } from '../roles';
import { checkEmail, checkNickname, checkPassword, rememberCredentials } from '../account-forms';
import { confirmDialog, toast } from '../utils';
import { disablePushNotifications, enablePushNotifications, isPushEnabled, pushSupported } from '../notifications';

export async function initMore() {
  renderNav('more');

  const loginView = document.getElementById('login-view') as HTMLElement;
  const moreView = document.getElementById('more-view') as HTMLElement;

  const username = await getCurrentUser();
  if (!username) {
    setupAuthForms();
    loginView.style.display = 'block';
    moreView.style.display = 'none';
    return;
  }

  loginView.style.display = 'none';
  moreView.style.display = 'block';
  (document.getElementById('account-username') as HTMLElement).textContent = (await getDisplayName()) ?? username;

  // Гостьовий акаунт (tier_1_subscriber) не має соціальних функцій (друзі/рекомендації) — ховаємо їх
  // (сервер у будь-якому разі віддає 403) і не питаємо про бейдж.
  const role = await getCurrentRole();
  const social = canAccess(role, 'social');
  if (!social) {
    for (const id of ['friends-link', 'recommendations-link']) document.getElementById(id)?.remove();
  }

  // Бейдж непрочитаних рекомендацій біля кнопки "Рекомендації".
  const recommendBadge = document.getElementById('recommend-badge') as HTMLElement | null;
  if (social && recommendBadge) {
    api
      .recommendations()
      .then((res) => {
        if (res.unreadCount > 0) {
          recommendBadge.textContent = res.unreadCount > 99 ? '99+' : String(res.unreadCount);
          recommendBadge.style.display = 'inline-flex';
        }
      })
      .catch(() => {
        /* не критично для решти сторінки */
      });
  }

  // Тумблер "Сповіщення про нові розділи" — PWA push, вимкнено за
  // замовчуванням (стан визначається наявністю активної підписки САМЕ в
  // цьому браузері: client/src/notifications.ts). Якщо браузер узагалі не
  // підтримує push (напр. немає HTTPS/localhost) — просто ховаємо рядок,
  // не морочачи користувача перемикачем, який однаково не запрацює.
  const pushRow = document.getElementById('push-notifications-row') as HTMLElement;
  const pushCheckbox = document.getElementById('push-notifications-checkbox') as HTMLInputElement;
  if (!pushSupported()) {
    pushRow.style.display = 'none';
  } else {
    isPushEnabled().then((enabled) => {
      pushCheckbox.checked = enabled;
    });
    pushCheckbox.addEventListener('change', async () => {
      const wantEnabled = pushCheckbox.checked;
      pushCheckbox.disabled = true;
      try {
        if (wantEnabled) {
          await enablePushNotifications();
          toast('Сповіщення про нові розділи увімкнено');
        } else {
          await disablePushNotifications();
          toast('Сповіщення вимкнено');
        }
      } catch (err) {
        pushCheckbox.checked = !wantEnabled; // відкатуємо перемикач — дія не вдалась
        toast((err as Error).message);
      } finally {
        pushCheckbox.disabled = false;
      }
    });
  }

  // Блок "Сервер" (статус, час роботи) — лише для звичайних акаунтів; гостьовому акаунту (tier_1_subscriber) його не показуємо.
  if (!isSubsRole(role)) document.getElementById('ctrlprof-link')?.remove(); // керування профілем — лише для гостьового акаунта
  if (isSubsRole(role)) {
    document.getElementById('server-section')?.remove();
  } else {
    (document.getElementById('server-section') as HTMLElement | null)?.style.removeProperty('display');
    const health = document.getElementById('health-info') as HTMLElement;
    try {
      const h = await fetch('/api/health').then((r) => r.json());
      health.textContent = `Активних джерел: ${h.plugins} • Час роботи сервера: ${formatUptime(h.uptime)}`;
    } catch {
      health.textContent = 'Не вдалося отримати статус сервера.';
    }
  }

  const logoutBtn = document.getElementById('logout-btn') as HTMLElement;
  logoutBtn.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: 'Вийти з акаунта?',
      message: 'Ви завжди зможете увійти знову зі своїми даними для входу.',
      confirmText: 'Вийти',
    });
    if (!ok) return;
    try {
      await api.logout();
    } catch (err) {
      toast((err as Error).message);
      return;
    }
    setAuthHint(null);
    location.href = '/more';
  });
}

// ---------------- авторизація: вхід / реєстрація / відновлення пароля в одному місці ----------------
//
// Замість вікон поверх сторінки поля форми просто міняються: показується одна з трьох <form>.
// Валідація на клієнті дзеркалить серверну (account-forms.ts), але сервер перевіряє все сам.

type AuthMode = 'login' | 'register' | 'forgot';

const byId = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

function showError(el: HTMLElement, text: string) {
  el.textContent = text;
  el.style.display = text ? 'block' : 'none';
}

const coarsePointer = () => window.matchMedia?.('(pointer: coarse)').matches ?? false;

function setupAuthForms() {
  const title = byId('auth-title');
  const forms: Record<AuthMode, HTMLElement> = { login: byId('login-form'), register: byId('register-form'), forgot: byId('forgot-form') };
  const titles: Record<AuthMode, string> = { login: 'Авторизація', register: 'Створення акаунта', forgot: 'Відновлення пароля' };
  const firstInput: Record<AuthMode, string> = { login: 'login-username', register: 'register-email', forgot: 'forgot-email' };

  function show(mode: AuthMode) {
    for (const m of Object.keys(forms) as AuthMode[]) forms[m].style.display = m === mode ? '' : 'none';
    title.textContent = titles[mode];
    for (const id of ['login-error', 'register-error', 'forgot-error']) showError(byId(id), '');
    // Комп'ютер: одразу в перше поле. Телефон: ні — клавіатура сама не з'являється й не зсуває сторінку.
    if (!coarsePointer()) byId<HTMLInputElement>(firstInput[mode]).focus();
  }

  setupLogin(show);
  setupRegister(show);
  setupForgot(show);
}

function setupLogin(show: (m: AuthMode) => void) {
  const usernameInput = byId<HTMLInputElement>('login-username');
  const passwordInput = byId<HTMLInputElement>('login-password');
  const errorEl = byId('login-error');
  const submitBtn = byId<HTMLButtonElement>('login-submit');

  async function submit() {
    const u = usernameInput.value.trim();
    const p = passwordInput.value;
    if (!u || !p) return showError(errorEl, 'Введіть email і пароль.');
    showError(errorEl, '');
    submitBtn.disabled = true;
    try {
      const res = await api.login(u, p);
      setAuthHint(res.username ?? u, res.role);
      await rememberCredentials(u, p); // зберігаємо те, що користувач вводить для входу (пошту)
      // Після входу першою відкривається вкладка "Більше".
      // replace, а не href: сторінка входу не лишається в історії (і "назад" не повертає на форму).
      location.replace('/more');
    } catch (err) {
      showError(errorEl, (err as Error).message || 'Невірні дані для входу.');
      submitBtn.disabled = false;
    }
  }

  byId('register-btn').addEventListener('click', (e) => {
    e.preventDefault();
    show('register');
  });
  byId('forgot-link').addEventListener('click', () => {
    byId<HTMLInputElement>('forgot-email').value = usernameInput.value.trim();
    show('forgot');
  });
  // Це <form>: Enter і кнопка "Увійти" шлють submit; сам запит робимо через fetch.
  byId('login-form').addEventListener('submit', (e) => {
    e.preventDefault();
    void submit();
  });
  usernameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      passwordInput.focus();
    }
  });
}

function setupRegister(show: (m: AuthMode) => void) {
  const emailInput = byId<HTMLInputElement>('register-email');
  const nickInput = byId<HTMLInputElement>('register-nickname');
  const passInput = byId<HTMLInputElement>('register-password');
  const confirmInput = byId<HTMLInputElement>('register-confirm');
  const errorEl = byId('register-error');
  const submitBtn = byId<HTMLButtonElement>('register-submit');

  byId('register-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = emailInput.value.trim();
    const nickname = nickInput.value.trim();
    const err = checkEmail(email) ?? checkNickname(nickname) ?? checkPassword(passInput.value, confirmInput.value);
    if (err) return showError(errorEl, err);
    showError(errorEl, '');
    submitBtn.disabled = true;
    try {
      const res = await api.register(email, nickname, passInput.value, confirmInput.value);
      setAuthHint(res.username, res.role);
      await rememberCredentials(email, passInput.value);
      location.replace('/more');
    } catch (e2) {
      showError(errorEl, (e2 as Error).message || 'Щось пішло не так. Спробуйте пізніше.');
      submitBtn.disabled = false;
    }
  });
  byId('register-back').addEventListener('click', () => {
    byId<HTMLInputElement>('login-username').value = emailInput.value.trim();
    show('login');
  });
}

/**
 * Відновлення пароля — три кроки в одній формі: 1) пошта → код у Telegram; 2) лише код (пошта вже відома й лежить
 * у прихованому полі); 3) новий пароль — його поля з'являються ЛИШЕ після того, як сервер прийняв код.
 */
function setupForgot(show: (m: AuthMode) => void) {
  type Step = 'email' | 'code' | 'password';
  const steps = Array.from(byId('forgot-form').querySelectorAll<HTMLElement>('[data-step]'));
  const note = byId('forgot-note');
  const errorEl = byId('forgot-error');
  const submitBtn = byId<HTMLButtonElement>('forgot-submit');
  const altBtn = byId<HTMLButtonElement>('forgot-alt');
  const emailInput = byId<HTMLInputElement>('forgot-email');
  const codeInput = byId<HTMLInputElement>('forgot-code');
  const passInput = byId<HTMLInputElement>('forgot-password');
  const confirmInput = byId<HTMLInputElement>('forgot-confirm');

  let step: Step = 'email';
  let email = '';
  let code = '';

  const texts: Record<Step, { submit: string; alt: string | null; note: string }> = {
    email: { submit: 'Надіслати код', alt: 'У мене вже є код', note: 'Вкажіть email — якщо до акаунта прив’язано Telegram, бот надішле код підтвердження.' },
    code: { submit: 'Далі', alt: 'Надіслати код знову', note: 'Введіть 6-значний код, який надіслав бот.' },
    password: { submit: 'Змінити пароль', alt: null, note: 'Придумайте новий пароль.' },
  };

  function go(next: Step, notice?: string) {
    step = next;
    for (const el of steps) el.style.display = el.dataset.step === next ? '' : 'none';
    for (const id of ['forgot-email-code', 'forgot-email-pass']) byId<HTMLInputElement>(id).value = email;
    submitBtn.textContent = texts[next].submit;
    altBtn.style.display = texts[next].alt ? '' : 'none';
    altBtn.textContent = texts[next].alt ?? '';
    note.textContent = notice ?? texts[next].note;
    showError(errorEl, '');
    submitBtn.disabled = false;
    const focusId = next === 'email' ? 'forgot-email' : next === 'code' ? 'forgot-code' : 'forgot-password';
    if (!coarsePointer()) byId<HTMLInputElement>(focusId).focus();
  }

  function reset() {
    email = '';
    code = '';
    for (const i of [emailInput, codeInput, passInput, confirmInput]) i.value = '';
    go('email');
  }

  // Кожного разу, коли форму відновлення відкривають, починаємо з першого кроку (пошта може бути підставлена з форми входу).
  byId('forgot-link').addEventListener('click', () => {
    email = '';
    code = '';
    for (const i of [codeInput, passInput, confirmInput]) i.value = '';
    go('email');
  });

  byId('forgot-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    showError(errorEl, '');
    submitBtn.disabled = true;
    try {
      if (step === 'email') {
        const v = emailInput.value.trim();
        const err = checkEmail(v);
        if (err) throw new Error(err);
        email = v;
        const res = await api.forgotPassword(v);
        go('code', res.message);
      } else if (step === 'code') {
        const c = codeInput.value.trim();
        if (!/^\d{6}$/.test(c)) throw new Error('Код складається з 6 цифр.');
        await api.checkResetCode(email, c); // сервер перевіряє код, але ще не витрачає його
        code = c;
        go('password');
      } else {
        const err = checkPassword(passInput.value, confirmInput.value);
        if (err) throw new Error(err);
        await api.resetPassword(email, code, passInput.value, confirmInput.value);
        await rememberCredentials(email, passInput.value);
        const doneEmail = email;
        reset();
        byId<HTMLInputElement>('login-username').value = doneEmail;
        show('login');
        toast('Пароль змінено. Увійдіть із новим паролем.');
        return;
      }
    } catch (err) {
      showError(errorEl, (err as Error).message || 'Щось пішло не так. Спробуйте пізніше.');
    } finally {
      submitBtn.disabled = false;
    }
  });

  altBtn.addEventListener('click', () => {
    if (step === 'email') {
      const v = emailInput.value.trim();
      const err = checkEmail(v);
      if (err) return showError(errorEl, err);
      email = v;
      go('code');
    } else if (step === 'code') {
      go('email');
    }
  });

  byId('forgot-back').addEventListener('click', () => {
    byId<HTMLInputElement>('login-username').value = email || emailInput.value.trim();
    reset();
    show('login');
  });

  go('email');
}

function formatUptime(sec: number): string {
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h} год ${m % 60} хв`;
  if (m > 0) return `${m} хв`;
  return `${Math.floor(sec)} с`;
}
